import { api } from './client'
import type {
  AdminActionsPage,
  AdminActivityReport,
  AdminControlState,
  AdminLeaguesReport,
  AdminMatchesWithEvents,
  AdminSummary,
  AdminUserDetail,
  AdminUserView,
  DataCoverage,
  EngagementReport,
  FeedEventView,
  FeedPage,
  OverrideView,
  OverrideWriteResult,
  ProviderRequestsReport,
  PurgeMockFriendsResult,
} from '../adminApi'
import type { FootballStatusResponse, ReplicaClockState } from '../footballStatus'

/**
 * The backoffice's client-side API surface.
 *
 * Every one of these is 403 for a non-admin server-side, and capability-gated by mode on top of
 * that — these wrappers are convenience, never the control. The bundle they belong to is only
 * ever loaded behind `identity.isAdmin`, which is defence in depth for the same reason.
 */

// ── Summary & audit log ───────────────────────────────────────────────────────────────────────

export function getAdminSummary(): Promise<AdminSummary> {
  return api.get<AdminSummary>('/admin/summary')
}

export function getAdminActions(options: { limit?: number; before?: number; action?: string } = {}) {
  const query = new URLSearchParams()
  if (options.limit !== undefined) query.set('limit', String(options.limit))
  if (options.before !== undefined) query.set('before', String(options.before))
  if (options.action) query.set('action', options.action)
  const suffix = query.toString()
  return api.get<AdminActionsPage>(`/admin/actions${suffix ? `?${suffix}` : ''}`)
}

// ── Event feed ────────────────────────────────────────────────────────────────────────────────

export function getFeedEvents(options: { since?: number; before?: number; limit?: number } = {}) {
  const query = new URLSearchParams()
  if (options.since !== undefined) query.set('since', String(options.since))
  if (options.before !== undefined) query.set('before', String(options.before))
  if (options.limit !== undefined) query.set('limit', String(options.limit))
  const suffix = query.toString()
  return api.get<FeedPage>(`/admin/events${suffix ? `?${suffix}` : ''}`)
}

/** The SSE URL. Built here so the one place that knows the path is the one place that knows it. */
export function feedStreamUrl(since: number): string {
  return `/api/admin/events/stream?since=${since}`
}

// ── Live matches ──────────────────────────────────────────────────────────────────────────────

export function getAdminMatches(): Promise<AdminMatchesWithEvents> {
  return api.get<AdminMatchesWithEvents>('/admin/matches')
}

// ── Season & gameweek control ─────────────────────────────────────────────────────────────────

export function getAdminControl(): Promise<AdminControlState> {
  return api.get<AdminControlState>('/admin/control')
}

export function getAdminClock(): Promise<ReplicaClockState> {
  return api.get<ReplicaClockState>('/admin/clock')
}

export function setAdminClock(change: { nowMs?: number; advanceMinutes?: number; speed?: number }) {
  return api.post<{ clock: ReplicaClockState }>('/admin/clock', change)
}

export function resetAdminClock(): Promise<ReplicaClockState> {
  return api.post<ReplicaClockState>('/admin/reset-clock')
}

export function jumpAdminClock(
  to: 'next-deadline' | 'next-kickoff' | 'next-fulltime' | 'gameweek-deadline',
  gameweek?: number
) {
  return api.post<{ jump: { label: string }; clock: ReplicaClockState }>('/admin/clock/jump', {
    to,
    gameweek,
  })
}

export function runGameweekAction(action: 'deadline' | 'reveal' | 'finalize', gameweek: number) {
  return api.post<{ ok: true; gameweek: number }>(`/admin/gameweek/${action}`, { gameweek })
}

export function rescoreGameweek(gameweek: number) {
  return api.post<{ gameweek: number; users: number; changed: unknown[] }>(
    '/admin/gameweek/rescore',
    { gameweek }
  )
}

export function simulateSeason(throughGameweek: number | undefined, confirm: string) {
  return api.post<{ ok: true }>('/admin/season/simulate', { throughGameweek, confirm })
}

export function pauseScheduler(reason: string) {
  return api.post<{ paused: true }>('/admin/scheduler/pause', { reason })
}

export function resumeScheduler() {
  return api.post<{ paused: false }>('/admin/scheduler/resume')
}

export function clearBackoff(resource: string) {
  return api.post<{ cleared: true }>('/admin/scheduler/clear-backoff', { resource })
}

export function forceSchedulerTick() {
  return api.post<{ ok: true }>('/admin/scheduler/tick')
}

// ── Manual overrides ──────────────────────────────────────────────────────────────────────────

export function getOverrides(): Promise<{ overrides: OverrideView[] }> {
  return api.get<{ overrides: OverrideView[] }>('/admin/overrides')
}

export interface OverrideDraft {
  status?: string | null
  homeScore?: number | null
  awayScore?: number | null
  halfTimeHomeScore?: number | null
  halfTimeAwayScore?: number | null
  kickoffUtc?: string | null
  reason: string
}

export function putOverride(fixtureId: string, draft: OverrideDraft): Promise<OverrideWriteResult> {
  return api.put<OverrideWriteResult>(`/admin/fixtures/${encodeURIComponent(fixtureId)}/override`, draft)
}

export function clearOverride(fixtureId: string) {
  return api.request<{ cleared: true; requiresRescore: boolean; gameweek: number | null }>(
    `/admin/fixtures/${encodeURIComponent(fixtureId)}/override`,
    { method: 'DELETE', body: JSON.stringify({}) }
  )
}

// ── Pipeline health ───────────────────────────────────────────────────────────────────────────

export function getAdminFootballStatus(): Promise<FootballStatusResponse> {
  return api.get<FootballStatusResponse>('/admin/football-status')
}

export function getProviderRequests(days = 7): Promise<ProviderRequestsReport> {
  return api.get<ProviderRequestsReport>(`/admin/provider-requests?days=${days}`)
}

export function getDataCoverage(): Promise<DataCoverage> {
  return api.get<DataCoverage>('/admin/coverage')
}

export function getSystemEvents(limit = 50): Promise<{ events: FeedEventView[] }> {
  return api.get<{ events: FeedEventView[] }>(`/admin/system-events?limit=${limit}`)
}

// ── People & engagement ───────────────────────────────────────────────────────────────────────

export function getEngagement(gameweek?: number): Promise<EngagementReport> {
  return api.get<EngagementReport>(
    `/admin/engagement${gameweek === undefined ? '' : `?gameweek=${gameweek}`}`
  )
}

export function getAdminUsers(search?: string): Promise<{ users: AdminUserView[] }> {
  return api.get<{ users: AdminUserView[] }>(
    `/admin/users${search ? `?q=${encodeURIComponent(search)}` : ''}`
  )
}

export function getAdminUser(id: string): Promise<AdminUserDetail> {
  return api.get<AdminUserDetail>(`/admin/users/${encodeURIComponent(id)}`)
}

export function renameUser(id: string, displayName: string, reason: string) {
  return api.put<{ displayName: string }>(`/admin/users/${encodeURIComponent(id)}/display-name`, {
    displayName,
    reason,
  })
}

/**
 * Delete a user and every row they own.
 *
 * `confirm` is the target's current display name, typed by the operator — the server checks it
 * again, so this wrapper carries it rather than deciding anything. `api.delete` sends no body, so
 * this is one of the few calls that reaches for the escape hatch.
 */
export function deleteUser(id: string, confirm: string, reason: string) {
  return api.request<{ deleted: true; tables: Record<string, number> }>(
    `/admin/users/${encodeURIComponent(id)}`,
    { method: 'DELETE', body: JSON.stringify({ confirm, reason }) }
  )
}

/**
 * Purge all 10 seeded mock-friend bots and everything they own.
 *
 * `confirm` is the fixed phrase "DELETE ALL BOTS", not a per-target name — there is no single row
 * to key the typed confirmation to here, unlike `deleteUser`.
 */
export function purgeMockFriends(reason: string) {
  return api.request<PurgeMockFriendsResult>('/admin/mock-friends', {
    method: 'DELETE',
    body: JSON.stringify({ confirm: 'DELETE ALL BOTS', reason }),
  })
}

export function getAdminLeagues(): Promise<AdminLeaguesReport> {
  return api.get<AdminLeaguesReport>('/admin/leagues')
}

export function setLeagueMotto(id: string, motto: string, reason: string) {
  return api.put<{ motto: string | null }>(`/admin/leagues/${encodeURIComponent(id)}/motto`, {
    motto,
    reason,
  })
}

export function getAdminActivity(): Promise<AdminActivityReport> {
  return api.get<AdminActivityReport>('/admin/activity')
}
